import { useState } from "react";
import { Download, HardDriveDownload, MessageSquareText, Share2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";
import { resolveAuthMediaUrl } from "@/lib/authMedia";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

// Post-completion export actions for finished videos in the gallery:
// Download · Save to Local Files · Social share · Send via SMS.
//
// Auth-gated media (/api/... URLs) is fetched WITH the bearer token via
// authMedia and exported from the resulting blob — a bare <a href> to those
// routes would 401.

async function mediaBlob(t: TFunction, url: string): Promise<Blob> {
  const resolved = await resolveAuthMediaUrl(url); // blob:/data:/plain passthrough
  const r = await fetch(resolved);
  if (!r.ok) throw new Error(t("media.exportFetchFailed", { status: r.status }));
  return r.blob();
}

export default function ExportActions({ url, filename = "chopshop-video.mp4", shareText = "Made with FaceShot ChopShop" }: {
  url: string;
  filename?: string;
  shareText?: string;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<string | null>(null);
  const run = (key: string, fn: () => Promise<void>) => async () => {
    if (busy) return;
    setBusy(key);
    try { await fn(); } catch { /* keep the gallery usable; the user can retry */ }
    setBusy(null);
  };

  const download = run("download", async () => {
    const blob = await mediaBlob(t, url);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 30_000);
    trackEvent('media_downloaded', { export_method: 'download' });
  });

  // Save to Local Files: real save-dialog where the browser supports it
  // (Chromium's showSaveFilePicker), download fallback everywhere else.
  const saveLocal = run("save", async () => {
    const picker = (window as unknown as { showSaveFilePicker?: (o: object) => Promise<{ createWritable: () => Promise<{ write: (b: Blob) => Promise<void>; close: () => Promise<void> }> }> }).showSaveFilePicker;
    const blob = await mediaBlob(t, url);
    if (picker) {
      const handle = await picker({ suggestedName: filename, types: [{ description: t("media.videoFileType"), accept: { "video/mp4": [".mp4"] } }] });
      const w = await handle.createWritable();
      await w.write(blob); await w.close();
    } else {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 30_000);
    }
    trackEvent('media_downloaded', { export_method: 'save_local' });
  });

  // Social upload: the OS/browser share sheet with the actual FILE where
  // supported (posts straight into TikTok/IG/WhatsApp pickers), link fallback.
  const social = run("share", async () => {
    const nav = navigator as Navigator & { canShare?: (d: { files?: File[] }) => boolean };
    try {
      const blob = await mediaBlob(t, url);
      const file = new File([blob], filename, { type: blob.type || "video/mp4" });
      if (nav.share && nav.canShare && nav.canShare({ files: [file] })) {
        await nav.share({ files: [file], title: shareText, text: shareText });
        trackEvent('media_shared', { share_method: 'native_file_share' });
        return;
      }
    } catch { /* fall through to the link share */ }
    if (navigator.share) {
      await navigator.share({ title: shareText, text: shareText, url: window.location.origin });
      trackEvent('media_shared', { share_method: 'native_link_share' });
    }
  });

  // SMS: opens the messaging app with a prefilled note (the video itself is
  // account-gated, so the text carries the site; attach the file via Share).
  const sms = () => {
    trackEvent('media_shared', { share_method: 'sms' });
    window.location.href = `sms:?&body=${encodeURIComponent(`${shareText} — ${window.location.origin}`)}`;
  };

  const b = (key: string, icon: React.ReactNode, label: string, onClick: () => void) => (
    <Button variant="outline" size="sm" onClick={onClick} disabled={busy !== null}>
      {busy === key ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : icon} {label}
    </Button>
  );

  return (
    <div className="flex flex-wrap gap-2">
      {b("download", <Download className="w-4 h-4 mr-2" />, t("media.download"), download)}
      {b("save", <HardDriveDownload className="w-4 h-4 mr-2" />, t("media.saveLocal"), saveLocal)}
      {b("share", <Share2 className="w-4 h-4 mr-2" />, t("media.socialUpload"), social)}
      {b("sms", <MessageSquareText className="w-4 h-4 mr-2" />, t("media.sendSms"), sms)}
    </div>
  );
}
